import { Outlet } from "react-router-dom";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Page } from "../../components/Page";
import {
  ContentWrapper,
  SidebarWrapper,
  Backdrop,
  MainContent,
} from "../../components/LayoutComponents";
import { AppNavbar } from "../components/AppNavbar";
import { StoreSidebar } from "./StoreSidebar";
import type { StoreId } from "@KassaroAB/shared/types/ids";
import { useGetStoreQuery } from "../../query/storesQuery";
import { NotFoundView } from "../NotFoundView";
import { LoadingContainer } from "../../components/LoadingContainer";
import { CatalogNavProvider } from "../catalog/CatalogNavContext";

interface StoreLayoutProps {
  selectedStoreId: StoreId | null;
  onStoreChange: (storeId: StoreId) => void;
}

export const StoreLayout = ({ selectedStoreId, onStoreChange }: StoreLayoutProps) => {
  const { t } = useTranslation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { data: store, isLoading } = useGetStoreQuery(selectedStoreId ?? undefined);

  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleSidebarClose = () => {
    setSidebarOpen(false);
  };

  const storeNavbar = (
    <AppNavbar
      selectedStoreId={selectedStoreId}
      onStoreChange={onStoreChange}
      onMenuClick={store ? handleSidebarToggle : undefined}
    />
  );

  if (selectedStoreId && isLoading) {
    return (
      <Page fullHeight centered navbar={storeNavbar}>
        <LoadingContainer message={t("overview.loadingStore")} />
      </Page>
    );
  }

  if (selectedStoreId && !store) {
    return (
      <NotFoundView
        title={false}
        subtitle={t("overview.storeNotFound")}
        message={false}
        navbar={storeNavbar}
      />
    );
  }

  return (
    <CatalogNavProvider>
      <Page
        fullHeight
        navbar={storeNavbar}
      >
        <ContentWrapper>
          <Backdrop $isOpen={sidebarOpen} onClick={handleSidebarClose} />
          <SidebarWrapper $isOpen={sidebarOpen}>
            <StoreSidebar storeId={selectedStoreId} onNavigate={handleSidebarClose} />
          </SidebarWrapper>
          <MainContent>
            <Outlet />
          </MainContent>
        </ContentWrapper>
      </Page>
    </CatalogNavProvider>
  );
};

